"use client"

// src/index.ts
import { noop, useCallbackRef } from "@yamada-ui/utils";
import { useCallback, useRef, useState } from "react";
var useDisclosure = ({
  defaultIsOpen = false,
  defaultOpen = defaultIsOpen,
  isOpen,
  open: controlledOpen = isOpen,
  timing,
  onClose: onCloseProp,
  onOpen: onOpenProp
} = {}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const timingRef = useRef(timing != null ? timing : "after");
  const handleOpen = useCallbackRef(onOpenProp);
  const handleClose = useCallbackRef(onCloseProp);
  const controlled = controlledOpen !== void 0;
  const open = controlledOpen !== void 0 ? controlledOpen : uncontrolledOpen;
  const onOpen = useCallback(
    async (...args) => {
      if (timingRef.current === "before") await handleOpen(...args);
      if (!controlled) setUncontrolledOpen(true);
      if (timingRef.current === "after") await handleOpen(...args);
    },
    [controlled, handleOpen, timingRef]
  );
  const onClose = useCallback(
    async (...args) => {
      if (timingRef.current === "before") await handleClose(...args);
      if (!controlled) setUncontrolledOpen(false);
      if (timingRef.current === "after") await handleClose(...args);
    },
    [controlled, handleClose, timingRef]
  );
  const onToggle = useCallback(
    async (...args) => !open ? onOpen(...args) : onClose(...args),
    [open, onOpen, onClose]
  );
  return {
    /**
     * @deprecated Use `open` instead.
     */
    isOpen: open,
    open,
    onClose,
    onOpen,
    onToggle
  };
};
var usePromiseDisclosure = ({
  disableCloseOnSuccess = false,
  error,
  ...rest
} = {}) => {
  const {
    open,
    onClose: onInternalClose,
    onOpen: onInternalOpen,
    onToggle
  } = useDisclosure(rest);
  const rejectRef = useRef(void 0);
  const resolveRef = useRef(noop);
  const onOpen = useCallback(
    async (...args) => {
      const promise = new Promise((resolve, reject) => {
        resolveRef.current = async (...args2) => {
          rejectRef.current = void 0;
          resolve();
          if (!disableCloseOnSuccess) await onInternalClose(...args2);
        };
        rejectRef.current = reject;
      });
      await onInternalOpen(...args);
      await promise;
    },
    [onInternalOpen, onInternalClose, disableCloseOnSuccess]
  );
  const onClose = useCallback(
    async (...args) => {
      var _a;
      (_a = rejectRef.current) == null ? void 0 : _a.call(rejectRef, error);
      await onInternalClose(...args);
    },
    [onInternalClose, error]
  );
  return {
    /**
     * @deprecated Use `open` instead.
     */
    isOpen: open,
    open,
    onClose,
    onOpen,
    onSuccess: resolveRef.current,
    onToggle
  };
};
var useLazyDisclosure = ({
  enabled,
  isSelected,
  mode = "unmount",
  selected = isSelected,
  wasSelected
}) => {
  if (!enabled) return true;
  if (selected) return true;
  if (mode === "keepMounted" && wasSelected) return true;
  return false;
};
export {
  useDisclosure,
  useLazyDisclosure,
  usePromiseDisclosure
};
//# sourceMappingURL=index.mjs.map